/**
 * Entity-type field module — modular-form replacement for the legacy entity-type input.
 *
 * Replaces:
 *  • Schema → packages/frontend/src/schema/workspaceSchema.ts:126-128
 *      entityType: yup
 *        .string<EntityTypeEnum>()
 *        .required('Please select an Entity Type'),
 *  • UI → packages/frontend/src/components/WorkspaceDetails/BusinessDetails/BusinessDetailsForm.tsx:255-290
 *      <Controller name="entityType" ... render={() => (<AppDropdown ... />)} />
 *      (legacy AppDropdown/react-daisyui; here a shadcn-style Select via FormField)
 *
 * Cross-field side-effects (clear represents-clients → Individual, re-derive
 * workspaceType) live in ./useEntityTypeDependencies.ts.
 * Behaviour change: always editable; `disabled` only for a verified AU business (FR-013).
 */
import { EntityTypeEnum } from '@sr-sdks/api-sdk-axios';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
} from '@sr/ui-library';
import { Control } from 'react-hook-form';
import * as yup from 'yup';
import { ENTITY_TYPE_OPTIONS } from '../../shared/defaults';
import { WORKSPACE_DETAILS_FORM_FIELDS } from '../../shared/fieldNames';
import { WorkspaceDetailsFormValues } from '../../shared/types';

interface EntityTypeFieldProps {
  /**
   * Selected business-number country. Gates the `Other` option, which legacy
   * offers on non-AU workspaces only.
   */
  businessNumberCountry: string;
  control: Control<WorkspaceDetailsFormValues>;
  /** R1: disabled when editing a verified AU business (see plan.md). */
  disabled?: boolean;
}

/**
 * `Other` is a non-AU-only option, matching legacy
 * (BusinessDetails/BusinessDetailsForm.tsx:271,294 — the enum loop skips
 * `Other` and appends it as its own item behind `currentCountry !== 'AU'`).
 * APP-2237 QA note 3: dropping it outright removed it from non-AU workspaces
 * too. Also kept in the list whenever it is the current value, so a workspace
 * already stored as `other` renders its label instead of an empty trigger.
 */
const entityTypeOptions = (businessNumberCountry: string, value: string) =>
  businessNumberCountry !== 'AU' || value === EntityTypeEnum.Other
    ? ENTITY_TYPE_OPTIONS
    : ENTITY_TYPE_OPTIONS.filter(
        (option) => option.value !== EntityTypeEnum.Other,
      );

export const EntityTypeField = ({
  businessNumberCountry,
  control,
  disabled,
}: EntityTypeFieldProps) => (
  <FormField
    name={WORKSPACE_DETAILS_FORM_FIELDS.entityType}
    control={control}
    render={({ field }) => (
      <FormItem>
        <FormLabel asterisk>Entity Type</FormLabel>
        <FormControl>
          <Select
            allowDeselect={false}
            disabled={disabled}
            options={entityTypeOptions(
              businessNumberCountry,
              field.value ?? '',
            )}
            placeholder="Select an entity type"
            value={field.value ?? ''}
            onValueChange={field.onChange}
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
);

EntityTypeField.schema = yup
  .string<EntityTypeEnum>()
  .required('Please select an Entity Type');
